import styled, { css } from 'styled-components';
import { DEEP_BORDER_BOX } from 'common';
import {
  SPACING_2,
  SPACING_4,
  SPACING_8,
  SPACING_12,
  SPACING_24,
  SPACING_32,
  SPACING_64
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { SHADOW_SPREAD_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/shadow_spread';
import BG_PATTERN_SVG from './animations/BG_pattern';
import { ANIMATION_ID } from '../SignInBackground';

/* Defaults */
const PADDING = SPACING_64; // rem
const INNER_WIDTH = '31rem'; // 496px

export const BREAKPOINT_XXXL = '@media only screen and (min-width: 3000px)';
export const BREAKPOINT_XXL = '@media only screen and (min-width: 1600px) and (max-width: 2999px)';
export const BREAKPOINT_XL = '@media only screen and (min-width: 1200px) and (max-width: 1599px)';
export const BREAKPOINT_L = '@media only screen and (min-width: 992px) and (max-width: 1199px)';
export const BREAKPOINT_M = '@media only screen and (min-width: 768px) and (max-width: 991px)';
export const BREAKPOINT_XS = '@media only screen and (min-width: 0px) and (max-width: 767px)';
export const BREAKPOINT_XS_M = '(min-width: 0px) and (max-width: 991px)'; // for use in window.matchMedia

const SPACING_1 = '0.0625rem';

const SMALLER_VIEW = css`
  [class^='styles__SignIn-'] {
    flex-direction: column-reverse;
  }
  [class^='styles__SignInContent-'] {
    width: 100%;
    min-width: 100%;
    flex-grow: 1;
  }
  [class^='styles__SignInBackground-'] {
    flex-grow: 0;
    height: 30%;
  }
  [class^='styles__SignInBody-'] {
    padding: ${SPACING_32};
  }
  [class^='styles__SignInBodyInner-'] [class^='styles__SignInLogo-'] {
    display: block;
  }
  [class^='styles__SignInHeader-'] [class^='styles__SignInLogo-'] {
    display: none;
  }
  [class^='styles__SignInHeader-'] {
    padding: 0;
  }
  [class^='styles__SignInFooter-'] {
    padding: ${SPACING_24} ${SPACING_32};
    padding-top: 0;
  }
  [class^='styles__SignInFooterInner-'] {
    max-width: ${INNER_WIDTH};
  }

  ${`#${ANIMATION_ID}-shield`} {
    max-width: 10%;
    max-width: 10%;
    max-width: 10%;
    max-width: 10%;
    max-width: 10%;
  }
  ${`#${ANIMATION_ID}-laptop`} {
    max-width: 20%;
  }
  ${`#${ANIMATION_ID}-knobs`} {
    max-width: 17%;
  }
  ${`#${ANIMATION_ID}-chat`} {
    max-width: 15%;
  }
  ${`#${ANIMATION_ID}-check`} {
    max-width: 15%;
  }
`;

const SignIn = styled.div`
  ${DEEP_BORDER_BOX}
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: ${props => props.theme.background};
  ${({ $rtl }) =>
    $rtl &&
    css`
      direction: rtl;
    `};
`;

const SignInLogo = styled.div`
  display: inline-flex;
`;
const SignInHeader = styled.div`
  display: flex;
  padding: ${PADDING} ${PADDING} 0 ${PADDING};
`;

const SignInBackgroundInner = styled.div`
    position: relative;
  .ccfk-signin-animation-elements {
    position: absolute;
    top:0;
    left: 0;
    width: 100%;
    height: 100%;
  }
    ${`#${ANIMATION_ID}-shield`} {
      top: initial;
      width: 15%;
      max-height: 184px; // pixels values necessary
      height: auto;
      bottom: 10%;
      left: 8%;
    }
    ${`#${ANIMATION_ID}-laptop`} {
      width: 38%;
      height: auto;
      max-height: 260px;
      margin: auto;
      top: 0; left: 0; bottom: 0; right: 0;
    }
    ${`#${ANIMATION_ID}-knobs`} {
      width: 20%;
      height: auto;
      max-height: 126px;
      top: initial;
      bottom: 8%;
      left: initial;
      right: 5%;
    }
    ${`#${ANIMATION_ID}-chat`} {
      width: 20%;
      height: auto;
      max-height: 260px;
      top: 5%;
      left: initial;
      right: 5%;
    }
    ${`#${ANIMATION_ID}-check`} {
      width: 20%;
      height: auto;
      max-height: 196px;
      top: 10%;
      left: 5%;
    }

  &:before {
    background: url('${BG_PATTERN_SVG}');
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    ${props =>
    props.dark &&
      css`
        filter: grayscale();
      `}
  }
`;

const SignInFooter = styled.div`
  padding: ${PADDING};
  padding-top: 0;
  display: flex;
  align-items: center;

  /* Language selector overrides */
  [data-id='ccfk-signin-language-selector'] div[class^='styles__SelectItemWrapper'] {
    padding: ${SPACING_4} ${SPACING_2};
  }
  [data-id='ccfk-signin-language-selector'] div[class^='styles__ListItemText'] {
    font-size: ${SPACING_12};
  }
`;

const SignInFooterInner = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const SignInBody = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  padding: ${PADDING};
  flex-direction: column;
`;

const SignInBodyInner = styled.div`
  width: 100%;
  max-width: ${INNER_WIDTH};
  margin: 0 auto;
`;

const SignInContent = styled.div`
  width: 50%;
  min-width: 50%;
  transition: width 0.5s linear;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const SignInPasswordPopup = styled.div`
  margin-top: 0.0625rem; /** 1px offset needed to align it flush with the blue focus border of text-input */
  background: ${props => props.theme.background};
  padding: ${SPACING_24};
  box-shadow: 0 0 ${SHADOW_SPREAD_2} 0 ${props => props.theme.shadowColorOne},
    0 ${SHADOW_SPREAD_2} ${SHADOW_SPREAD_2} 0 ${props => props.theme.shadowColorTwo};
`;

const SignInBackground = styled.div`
  flex-grow: 1;
  overflow: hidden;
  ${({ $background }) =>
    $background &&
    css`
      background: url(${$background}) no-repeat center center;
      background-repeat: no-repeat;
      background-size: cover;
    `};
`;

const SignInContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
  /** Hide inner content logo by default */
  [class^='styles__SignInBodyInner-'] [class^='styles__SignInLogo-'] {
    display: none;
  }
  ${BREAKPOINT_XXXL} {
    [class^='styles__SignIn-'] {
      width: 120rem; // 1920px
      height: 67.5rem; // 1080px
      box-shadow: ${props => props.theme.shadowColorOne} 0 0 ${SPACING_1} 0,
        ${props => props.theme.shadowColorTwo} 0 ${SPACING_1} ${SPACING_1} 0;
    }
    [class^='styles__SignInContent-'] {
      width: 50%;
      min-width: 50%;
    }
  }
  ${BREAKPOINT_XXL} {
    [class^='styles__SignInContent-'] {
      width: 50%;
      min-width: 50%;
    }
  }
  ${BREAKPOINT_L} {
    [class^='styles__SignInHeader-'] {
      padding: 0;
    }
    [class^='styles__SignInBodyInner-'] [class^='styles__SignInLogo-'] {
      display: block;
      padding-bottom: ${SPACING_8};
    }
    [class^='styles__SignInHeader-'] [class^='styles__SignInLogo-'] {
      display: none;
    }
  }
  ${BREAKPOINT_M} {
    ${SMALLER_VIEW}
  }
  ${BREAKPOINT_XS} {
    ${SMALLER_VIEW}
    [class^='styles__SignInContent-'] {
      min-width: 100%;
    }
    [class^='styles__SignInBodyInner-'],
    [class^='styles__SignInFooterInner-'] {
      margin: 0 auto;
    }
  }
`;

export default {
  SignIn,
  SignInContainer,
  SignInBody,
  SignInBodyInner,
  SignInContent,
  SignInBackgroundInner,
  SignInLogo,
  SignInHeader,
  SignInFooter,
  SignInFooterInner,
  SignInPasswordPopup,
  SignInBackground
};
